import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, Star, Save, Home as HomeIcon, Briefcase, ImageIcon } from "lucide-react";

export default function AdminTestimonials() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null); // testimonial being edited or "new"
  const load = () => api.get("/admin/testimonials").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await api.delete(`/admin/testimonials/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const move = async (idx, dir) => {
    const arr = [...rows];
    const t = idx + dir;
    if (t < 0 || t >= arr.length) return;
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    setRows(arr);
    await api.post("/admin/testimonials/reorder", { ids: arr.map(x => x.id) });
    toast.success("Order updated");
  };

  return (
    <>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Testimonials</h1>
          <p className="text-sm text-muted-foreground mt-1">Feature customer voices on your Home and Services pages.</p>
        </div>
        <button onClick={() => setEditing({ id: null, name: "", role: "", company: "", quote: "", rating: 5, photo_url: null, featured_home: true, featured_services: false, order: rows.length })}
          className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-testimonial-new">
          <Plus className="h-4 w-4"/> New testimonial
        </button>
      </div>

      {editing && <TestimonialEditor initial={editing} onClose={() => setEditing(null)} onSaved={load} />}

      <div className="mt-6 grid gap-3">
        {rows.map((r, idx) => (
          <div key={r.id} className="card-elevated p-5 flex items-start gap-4" data-testid={`admin-testimonial-row-${r.id}`}>
            <div className="flex flex-col gap-1 pt-1">
              <button onClick={() => move(idx, -1)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5" aria-label="Move up" data-testid={`admin-testimonial-up-${r.id}`}>↑</button>
              <GripVertical className="h-4 w-4 text-muted-foreground" />
              <button onClick={() => move(idx, 1)} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/5" aria-label="Move down" data-testid={`admin-testimonial-down-${r.id}`}>↓</button>
            </div>
            {r.photo_url ? (
              <img src={r.photo_url} alt={r.name} className="h-12 w-12 rounded-full object-cover border border-black/10 dark:border-white/10" />
            ) : (
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-[#1565FF] to-[#4FC3F7] grid place-items-center text-white font-semibold">
                {(r.name || "?").split(" ").map(s => s[0]).join("").slice(0,2)}
              </div>
            )}
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold tracking-tight">{r.name}</span>
                <span className="text-xs text-muted-foreground">{[r.role, r.company].filter(Boolean).join(" · ")}</span>
                <span className="flex items-center gap-0.5 ml-2">
                  {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-3 w-3 fill-[#1565FF] text-[#1565FF]" />)}
                </span>
              </div>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">"{r.quote}"</p>
              <div className="mt-2 flex gap-2 text-xs">
                {r.featured_home && <span className="rounded-full bg-[#1565FF]/10 text-[#1565FF] px-2 py-0.5 inline-flex items-center gap-1"><HomeIcon className="h-3 w-3"/> Home</span>}
                {r.featured_services && <span className="rounded-full bg-[#4FC3F7]/10 text-[#4FC3F7] px-2 py-0.5 inline-flex items-center gap-1"><Briefcase className="h-3 w-3"/> Services</span>}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={() => setEditing(r)} className="btn-ghost !py-1.5 !px-3 !text-xs" data-testid={`admin-testimonial-edit-${r.id}`}>Edit</button>
              <button onClick={() => del(r.id)} className="text-xs text-destructive inline-flex items-center gap-1" data-testid={`admin-testimonial-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5"/> Delete</button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-center text-sm text-muted-foreground py-6">No testimonials yet.</p>}
      </div>
    </>
  );
}

function TestimonialEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(initial);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) return toast.error("Please upload an image under 500KB");
    const reader = new FileReader();
    reader.onload = () => setF({ ...f, photo_url: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: f.name, role: f.role || null, company: f.company || null,
        quote: f.quote, rating: parseInt(f.rating) || 5,
        photo_url: f.photo_url || null,
        featured_home: !!f.featured_home, featured_services: !!f.featured_services,
        order: parseInt(f.order) || 0,
      };
      if (f.id) await api.put(`/admin/testimonials/${f.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      toast.success("Saved");
      onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={save} className="mt-6 card-elevated p-6 grid gap-3 md:grid-cols-2" data-testid="admin-testimonial-editor">
      <div className="md:col-span-2 flex items-center gap-4">
        {f.photo_url ? (
          <img src={f.photo_url} alt="preview" className="h-14 w-14 rounded-full object-cover border border-black/10 dark:border-white/10" />
        ) : (
          <div className="h-14 w-14 rounded-full bg-black/5 dark:bg-white/5 grid place-items-center text-muted-foreground"><ImageIcon className="h-5 w-5"/></div>
        )}
        <div>
          <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="admin-testimonial-file" />
          <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-1.5 !px-3 !text-xs">Upload photo (max 500KB)</button>
          {f.photo_url && <button type="button" onClick={() => setF({...f, photo_url: null})} className="ml-2 text-xs text-destructive">Remove</button>}
        </div>
      </div>
      <input required placeholder="Name" value={f.name} onChange={(e) => setF({...f, name: e.target.value})} data-testid="admin-testimonial-name"
        className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
      <input placeholder="Role" value={f.role || ""} onChange={(e) => setF({...f, role: e.target.value})} data-testid="admin-testimonial-role"
        className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
      <input placeholder="Company" value={f.company || ""} onChange={(e) => setF({...f, company: e.target.value})} data-testid="admin-testimonial-company"
        className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
      <select value={f.rating} onChange={(e) => setF({...f, rating: e.target.value})} data-testid="admin-testimonial-rating"
        className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm">
        {[5,4,3,2,1].map(v => <option key={v} value={v}>{v} star{v>1?"s":""}</option>)}
      </select>
      <textarea required placeholder="Quote" rows={3} value={f.quote} onChange={(e) => setF({...f, quote: e.target.value})} data-testid="admin-testimonial-quote"
        className="md:col-span-2 rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-white/5 px-3 py-2 text-sm" />
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured_home} onChange={(e) => setF({...f, featured_home: e.target.checked})} data-testid="admin-testimonial-fh"/> Feature on Home</label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.featured_services} onChange={(e) => setF({...f, featured_services: e.target.checked})} data-testid="admin-testimonial-fs"/> Feature on Services</label>
      <div className="md:col-span-2 flex gap-2">
        <button disabled={loading} className="btn-primary !py-2 !px-4 !text-sm" data-testid="admin-testimonial-save"><Save className="h-4 w-4"/> Save</button>
        <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-4 !text-sm">Cancel</button>
      </div>
    </form>
  );
}
